import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { Building2, Users, FileText, CheckSquare, Mail, DollarSign, Tag } from "lucide-react";
import type { ObjectConfig } from "./types";
import { ALL_RELATIONSHIPS } from "./types";

// ─── Props ───────────────────────────────────────────────────────────────────

interface RelationshipDiagramProps {
  objects: ObjectConfig[];
  integrationName: string;
}

// ─── Object metadata ─────────────────────────────────────────────────────────

const OBJECT_ICONS: Record<string, typeof Building2> = {
  accounts: Building2,
  contacts: Users,
  notes: FileText,
  tasks: CheckSquare,
  emails: Mail,
  deals: DollarSign,
};

const OBJECT_COLORS: Record<string, string> = {
  accounts: "bg-primary/10 text-primary",
  contacts: "bg-status-in-progress/10 text-status-in-progress",
  notes: "bg-status-todo/10 text-status-todo",
  tasks: "bg-status-active/10 text-status-active",
  emails: "bg-status-info/10 text-status-info",
  deals: "bg-status-cancelled/10 text-status-cancelled",
};

// Positions as percentages (x%, y%) — center of each card
const CARD_POSITIONS: Record<string, { x: number; y: number }> = {
  accounts: { x: 50, y: 12 },
  contacts: { x: 18, y: 45 },
  deals: { x: 82, y: 45 },
  notes: { x: 18, y: 82 },
  tasks: { x: 50, y: 82 },
  emails: { x: 82, y: 82 },
};

const CARD_W = 150;
const CARD_H = 56;

// ─── Component ───────────────────────────────────────────────────────────────

export const RelationshipDiagram = ({
  objects,
  integrationName,
}: RelationshipDiagramProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const updateWidth = useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [updateWidth]);

  const enabledObjects = useMemo(
    () => objects.filter((o) => o.enabled),
    [objects],
  );

  const enabledKeys = useMemo(
    () => new Set(enabledObjects.map((o) => o.key)),
    [enabledObjects],
  );

  const visibleRelationships = useMemo(
    () =>
      ALL_RELATIONSHIPS.filter(
        (r) => enabledKeys.has(r.from) && enabledKeys.has(r.to),
      ),
    [enabledKeys],
  );

  const containerH =
    enabledObjects.length <= 2
      ? 160
      : enabledObjects.length <= 4
        ? 280
        : 360;

  // Calculate pixel positions from percentages
  const positions = useMemo(() => {
    const pos: Record<string, { cx: number; cy: number }> = {};
    if (!containerWidth) return pos;

    if (enabledObjects.length === 1) {
      pos[enabledObjects[0].key] = {
        cx: containerWidth / 2,
        cy: containerH / 2,
      };
      return pos;
    }

    if (enabledObjects.length === 2) {
      const keys = enabledObjects.map((o) => o.key);
      pos[keys[0]] = { cx: containerWidth * 0.3, cy: containerH / 2 };
      pos[keys[1]] = { cx: containerWidth * 0.7, cy: containerH / 2 };
      return pos;
    }

    enabledObjects.forEach((obj) => {
      const preset = CARD_POSITIONS[obj.key] || { x: 50, y: 50 };
      pos[obj.key] = {
        cx: (preset.x / 100) * containerWidth,
        cy: (preset.y / 100) * containerH,
      };
    });

    return pos;
  }, [enabledObjects, containerWidth, containerH]);

  // ─── Empty state ─────────────────────────────────────────────────────────

  if (enabledObjects.length === 0) {
    return (
      <div className="border border-border rounded-lg p-6 text-center">
        <p className="text-[13px] text-muted-foreground">
          Enable objects above to see how they relate to each other.
        </p>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border bg-muted/20">
        <p className="text-[12px] font-medium text-foreground">
          Object Relationships
        </p>
        <p className="text-[11px] text-muted-foreground">
          How your enabled {integrationName} objects connect in Hyperengage
        </p>
      </div>

      <div
        ref={containerRef}
        className="relative"
        style={{ height: containerH }}
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              "radial-gradient(circle, #d0d0d0 0.8px, transparent 0.8px)",
            backgroundSize: "20px 20px",
            opacity: 0.4,
          }}
        />

        {containerWidth > 0 && (
          <svg
            className="absolute inset-0 z-[1] pointer-events-none"
            width={containerWidth}
            height={containerH}
          >
            {visibleRelationships.map((rel, i) => {
              const from = positions[rel.from];
              const to = positions[rel.to];
              if (!from || !to) return null;

              return (
                <line
                  key={i}
                  x1={from.cx}
                  y1={from.cy}
                  x2={to.cx}
                  y2={to.cy}
                  stroke="#c0c0c0"
                  strokeWidth={1.5}
                  strokeDasharray={
                    rel.type === "many_to_one" ? "6 4" : undefined
                  }
                />
              );
            })}
          </svg>
        )}

        {enabledObjects.map((obj) => {
          const pos = positions[obj.key];
          if (!pos) return null;

          const Icon = OBJECT_ICONS[obj.key] || Tag;
          const colorClass = OBJECT_COLORS[obj.key] || "bg-muted text-muted-foreground";
          const fieldCount = obj.traitMappings.length;

          return (
            <div
              key={obj.key}
              className="absolute z-[2] bg-white border border-border rounded-lg px-3 py-2 shadow-card flex items-center gap-2.5"
              style={{
                left: pos.cx - CARD_W / 2,
                top: pos.cy - CARD_H / 2,
                width: CARD_W,
                height: CARD_H,
              }}
            >
              <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-medium text-foreground truncate">
                    {obj.label}
                  </span>
                  {obj.isQuantitative && (
                    <span className="text-[9px] font-medium text-muted-foreground bg-muted rounded px-1 py-0.5 flex-shrink-0">
                      S
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <Tag className="h-2.5 w-2.5" />
                  {fieldCount > 0 ? `${fieldCount} fields` : "No fields"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-2 border-t border-border flex items-center gap-5 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-0 border-t-[1.5px] border-border-strong" />
          <span>M:N (Many to Many)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-0 border-t-[1.5px] border-dashed border-border-strong" />
          <span>1:N (One to Many)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-medium bg-muted rounded px-1 py-0.5">
            S
          </span>
          <span>Structured (field mapping)</span>
        </div>
      </div>
    </div>
  );
};
